import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ManualRegistrationForm from "./admin/ManualRegistrationForm";
import HubEmailsPanel from "./admin/HubEmailsPanel";
import ProgrammeEditor from "./admin/ProgrammeEditor";
import FacultyEditor from "./admin/FacultyEditor";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("registrations");
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/registrations/admin/all`, {
        params: { page, limit: 20, status_filter: filter },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (response.data.success) {
        setRegistrations(response.data.data);
        setStats(response.data.stats);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchRegistrations();
  }, [page, filter]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin");
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.get(`${API}/registrations/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lirss_registrations_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this registration?")) return;
    
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/registrations/admin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRegistrations();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const [resendingId, setResendingId] = useState(null);

  const handleResendWebhook = async (id) => {
    if (!window.confirm("Re-send the outbound webhook for this registration?")) return;
    setResendingId(id);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await axios.post(
        `${API}/registrations/admin/${id}/resend-webhook`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.delivered) {
        window.alert(`Webhook delivered (HTTP ${response.data.statusCode || 200}).`);
      } else {
        window.alert(`Webhook FAILED: ${response.data.error || "unknown error"}`);
      }
      fetchRegistrations();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      window.alert(`Resend failed: ${msg}`);
    } finally {
      setResendingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-white">LIRSS Admin Dashboard</h1>
            <p className="text-slate-400 text-sm">Manage conference registrations</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleExportCSV}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
              data-testid="export-csv-btn"
            >
              Export CSV
            </button>
            <button
              onClick={handleLogout}
              className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
              data-testid="logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700">
          {[
            { key: "registrations", label: "Registrations" },
            { key: "manual", label: "Manual Registration" },
            { key: "hubEmails", label: "Delegate Hub Emails" },
            { key: "programme", label: "Programme" },
            { key: "faculty", label: "Faculty" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-all ${
                activeTab === tab.key
                  ? "border-emerald-500 text-white"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`admin-tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "manual" && <ManualRegistrationForm />}
        {activeTab === "hubEmails" && <HubEmailsPanel />}
        {activeTab === "programme" && <ProgrammeEditor />}
        {activeTab === "faculty" && <FacultyEditor />}

        {activeTab === "registrations" && (
        <>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800/60 rounded-xl p-6 border border-slate-700" data-testid="stat-total">
            <div className="text-slate-400 text-sm">Total Registrations</div>
            <div className="text-3xl font-bold text-white mt-1">{stats.total}</div>
          </div>
          <div className="bg-emerald-900/40 rounded-xl p-6 border border-emerald-700" data-testid="stat-completed">
            <div className="text-emerald-400 text-sm">Completed Payments</div>
            <div className="text-3xl font-bold text-emerald-300 mt-1">{stats.completed}</div>
          </div>
          <div className="bg-amber-900/40 rounded-xl p-6 border border-amber-700" data-testid="stat-pending">
            <div className="text-amber-400 text-sm">Pending Payments</div>
            <div className="text-3xl font-bold text-amber-300 mt-1">{stats.pending}</div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          {["all", "completed", "pending"].map((f) => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === f
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-700 text-slate-300 hover:bg-slate-600"
              }`}
              data-testid={`filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-slate-800/60 rounded-xl border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : registrations.length === 0 ? (
            <div className="p-8 text-center text-slate-400">No registrations found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="registrations-table">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Webhook</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-300 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {registrations.map((reg) => {
                    return (
                    <tr key={reg._id} className="hover:bg-slate-700/30">
                      <td className="px-4 py-3 text-sm text-slate-300">{formatDate(reg.submittedAt)}</td>
                      <td className="px-4 py-3 text-sm text-white">{reg.title} {reg.firstName} {reg.lastName}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">{reg.email}</td>
                      <td className="px-4 py-3 text-sm text-slate-300">
                        <span className={`px-2 py-1 rounded text-xs ${
                          reg.registrationType?.includes("Combo") 
                            ? "bg-purple-500/20 text-purple-300" 
                            : "bg-slate-600 text-slate-300"
                        }`}>
                          {reg.registrationType || "LIRSS Only"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-300">{reg.delegateType}</td>
                      <td className="px-4 py-3 text-sm text-white font-medium">£{reg.amount}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          reg.paymentStatus === "completed"
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-amber-500/20 text-amber-300"
                        }`}>
                          {reg.paymentStatus}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {reg.webhookSent ? (
                          <span
                            className="px-2 py-1 rounded text-xs font-medium bg-emerald-500/20 text-emerald-300"
                            title={reg.webhookSentAt ? `Sent ${formatDate(reg.webhookSentAt)}` : ""}
                            data-testid={`webhook-status-sent-${reg._id}`}
                          >
                            Sent
                          </span>
                        ) : reg.webhookLastError ? (
                          <span
                            className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-300 cursor-help"
                            title={reg.webhookLastError}
                            data-testid={`webhook-status-failed-${reg._id}`}
                          >
                            Failed
                          </span>
                        ) : reg.paymentStatus === "completed" ? (
                          <span
                            className="px-2 py-1 rounded text-xs font-medium bg-slate-600 text-slate-300"
                            data-testid={`webhook-status-pending-${reg._id}`}
                          >
                            Pending
                          </span>
                        ) : (
                          <span className="text-slate-500 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-3">
                          {reg.paymentStatus === "completed" && (
                            <button
                              onClick={() => handleResendWebhook(reg._id)}
                              disabled={resendingId === reg._id}
                              className="text-sky-400 hover:text-sky-300 text-xs disabled:opacity-50"
                              data-testid={`resend-webhook-${reg._id}`}
                              title="Re-fire the outbound webhook for this registration"
                            >
                              {resendingId === reg._id ? "Sending…" : "Resend Webhook"}
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(reg._id)}
                            className="text-red-400 hover:text-red-300 text-xs"
                            data-testid={`delete-${reg._id}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 p-4 border-t border-slate-700">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 bg-slate-700 rounded text-slate-300 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-slate-400">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 bg-slate-700 rounded text-slate-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
        </>
        )}

        {/* Footer */}
        <footer className="text-center mt-8 text-slate-600 text-xs">
          © 2026 lirss.org. All rights reserved.
        </footer>
      </main>
    </div>
  );
};

export default AdminDashboard;
